import React, {useEffect} from 'react';
import axios from "axios";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";


import ColAtm from "../../../../atomic/Col/Col";
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import LabelAtm from "../../../../atomic/Label/Label";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import {UPDATE_PLACE_PHOTO, PLACE_PHOTO_ORDER} from "../../../../../constants/api.constants";
import {getAppLang, getProperty, getUserToken} from "../../../../../utils";
import useFlipOnReorder from "../../../../../utils/useFlipOnReorder";

import {AllCheckerCheckbox, CheckboxGroup} from "@createnl/grouped-checkboxes";
import MultiPhotoSelect from "../../../../molecules/MultiPhotoSelect/MultiPhotoSelect";
import {getPlacePhoto} from "../../../../../redux/actions/Content/Places/placesActions"

import {
    DndContext,
    closestCenter,
    MouseSensor,
    TouchSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    rectSortingStrategy,
    sortableKeyboardCoordinates,
    arrayMove,
} from '@dnd-kit/sortable';
import SortablePhotoSelect from "../../../../molecules/PhotoSelect/SortablePhotoSelect";

const EMPTY_PHOTOS = [];



const PlacePhotoList = props => {

    const placeList               = useSelector(props => props.getPlaceListReducer.placeList);
    const placePhotoList          = useSelector(props => props.getPlacePhotoListReducer.placePhotoList?.get_place_photos);
    const placePhotoListFetching  = useSelector(props => props.getPlacePhotoListReducer.fetching);
    const [expanded, setExpanded] = React.useState(false);
    // Seçili fotoğrafların kullanıcı tarafından belirlenen sırası (id listesi)
    const [photoOrder, setPhotoOrder] = React.useState([]);
    // Seçim state'i; checkbox'lar buna bağlı (controlled). Seçim değişince fotoğraf
    // sıralanabilir grup ile sabit grup arasında anında yer değiştirir.
    const [selectedIds, setSelectedIds] = React.useState(() => new Set());
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const sensors = useSensors(
        // 8px eşiği olmadan sürükleme, fotoğrafa tıklayıp seçme davranışını yutar
        useSensor(MouseSensor, {activationConstraint: {distance: 8}}),
        // Sürükleme tutamaçtan yapılıyor (touch-action:none), scroll çakışması yok;
        // bu yüzden çok kısa bir gecikme yeterli — sadece kazara dokunuşu ayıklar, hız hissi verir.
        useSensor(TouchSensor, {activationConstraint: {delay: 50, tolerance: 8}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    );

    const photos = placePhotoList || EMPTY_PHOTOS;

    // Gruplama API'den gelen is_selected'a değil, canlı seçim state'ine bakar
    const selectedPhotos = React.useMemo(
        () => photos.filter( (photo) => selectedIds.has(photo.id) ),
        [photos, selectedIds]
    );
    const unselectedPhotos = React.useMemo(
        () => photos.filter( (photo) => !selectedIds.has(photo.id) ),
        [photos, selectedIds]
    );

    // Başka bir mekan açıldığında (yeni liste geldiğinde) seçim ve manuel sıra API verisine göre kurulur
    useEffect(() => {
        const initialPhotos = placePhotoList || EMPTY_PHOTOS;

        const selected = initialPhotos.filter( (photo) => photo.is_selected === true );

        setSelectedIds(new Set( selected.map( (photo) => photo.id ) ));

        // Kaydedilmiş sıra sort_order'dan yüklenir (backend alanı ekleyince dolar);
        // sort_order'ı olmayan seçili fotoğraflar photoOrder'a girmez, sona eklenir.
        setPhotoOrder(
            selected
                .filter( (photo) => photo.sort_order !== null && photo.sort_order !== undefined )
                .sort( (a, b) => a.sort_order - b.sort_order )
                .map( (photo) => photo.id )
        );
    }, [placePhotoList]);

    // photoOrder'daki sıra uygulanır; listede olmayan id'ler elenir,
    // sırası belirlenmemiş seçili fotoğraflar sona eklenir
    const orderedSelectedPhotos = React.useMemo(() => {
        const photosById = new Map(selectedPhotos.map( (photo) => [photo.id, photo] ));
        const ordered = photoOrder.map( (id) => photosById.get(id) ).filter(Boolean);
        const orderedIds = new Set(ordered.map( (photo) => photo.id ));

        return ordered.concat(selectedPhotos.filter( (photo) => !orderedIds.has(photo.id) ));
    }, [selectedPhotos, photoOrder]);

    // Seçili ve seçili olmayanlar TEK listede render edilir. İki ayrı map olsaydı fotoğraf
    // grup değiştirirken unmount/mount olur, DOM düğümü yok olacağı için animasyon yapılamazdı.
    const displayPhotos = React.useMemo(
        () => orderedSelectedPhotos.concat(unselectedPhotos),
        [orderedSelectedPhotos, unselectedPhotos]
    );

    const photoGridRef = React.useRef(null);
    useFlipOnReorder(photoGridRef, selectedIds);

    const handleChange = (placeId) => (event, isExpanded) => {
        setExpanded(isExpanded ? placeId : false);
        if(isExpanded !== false){
            dispatch(getPlacePhoto(placeId))
        }
    };

    const handleDragEnd = (event, placeId) => {
        const {active, over} = event;

        if( !over || active.id === over.id ){
            return;
        }

        const currentIds = orderedSelectedPhotos.map( (photo) => photo.id );
        const oldIndex = currentIds.indexOf(active.id);
        const newIndex = currentIds.indexOf(over.id);

        if( oldIndex === -1 || newIndex === -1 ){
            return;
        }

        const nextOrder = arrayMove(currentIds, oldIndex, newIndex);

        setPhotoOrder(nextOrder);
        savePlacePhotoOrder(placeId, nextOrder);
    };

    // Sürükleme bitince o mekandaki seçili fotoğrafların tamamını sıralı (1..N) olarak backend'e yazar.
    const savePlacePhotoOrder = (placeId, orderedIds) => {
        const data = ({
            params: {
                property_id: getProperty(),
                place_id: placeId,
                property_place_photo: orderedIds.map( (id, index) => (
                    { id: +id, sort_order: index + 1 }
                ))
            }
        });

        axios.post(PLACE_PHOTO_ORDER, data,{
            headers: {
                "Accept": "application/json",
                authToken: getUserToken(),
                "Content-Type": "application/json",
                "language": getAppLang(),
            }
        }).then((result) => {
        })
            .catch(function (error) {
                console.log(error);
            });
    };

    // Fotoğrafın seçimi değişti: state'i güncelle + API'ye bildir.
    // Seçilen fotoğraf sıralanabilir grubun sonuna eklenir, seçimi kaldırılan alt gruba düşer.
    const handlePhotoToggle = (e, placeId) => {
        // React 16 event pooling: değerler updater'a girmeden önce okunmalı
        const photoValue = e.target.value;
        const isChecked  = e.target.checked;

        if( photoValue === undefined || isChecked === undefined ){
            return;
        }

        const toggledIds = String(photoValue).split(',').map( (x) => +x );

        setSelectedIds( (prevSelectedIds) => {
            const nextSelectedIds = new Set(prevSelectedIds);

            toggledIds.forEach( (id) => {
                if(isChecked){
                    nextSelectedIds.add(id);
                }else{
                    nextSelectedIds.delete(id);
                }
            });

            return nextSelectedIds;
        });

        updateRoomPhoto(e, placeId);
    };

    // Seçili fotoğraflar üstte durur ve yalnızca kendi aralarında (tutamaçtan) sürüklenerek sıralanır;
    // seçili olmayanlar altta sabit kalır.
    const RoomPhotoList = ( placeId ) => {

        if(placePhotoList && placePhotoList.length === 0 ){
            return;
        }

        if( expanded === false || placePhotoListFetching !== false ){
            return;
        }

        return (
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={ (event) => handleDragEnd(event, placeId) }
            >
                <SortableContext
                    items={ displayPhotos.map( (photo) => photo.id ) }
                    strategy={rectSortingStrategy}
                >
                    {
                        displayPhotos.map( (photo) => {
                            const isSelected = selectedIds.has(photo.id);

                            return (
                                <SortablePhotoSelect
                                    key={photo.id}
                                    sortableId={photo.id}
                                    sortableDisabled={!isSelected}
                                    checkboxId={photo.id}
                                    className="channel-box"
                                    colClassname={ isSelected ? "room-photo-grid sortable-photo" : "room-photo-grid" }
                                    blockClass="photo-select-border-box channel-border-box"
                                    src={ photo.photo_url_thump}
                                    value={photo.id}
                                    checked={isSelected}
                                    onChange={ (e) => handlePhotoToggle(e, placeId)}
                                    labelClass="fact-name"
                                    htmlFor={photo.id}
                                />
                            );
                        })
                    }
                </SortableContext>
            </DndContext>
        );
    };

    const RoomRecommendedPhotoList = ( roomId ) => {

        if(placePhotoList && placePhotoList.length === 0 ){
            return;
        }
        let roomPhotoListBox = '';
        if( expanded !== false && placePhotoListFetching === false  && placePhotoList.show_recommended === true){

            roomPhotoListBox = placePhotoList.map( (photo, key) => (

                    photo.is_recommended === true ? (
                        <MultiPhotoSelect
                            checkboxId={photo.id}
                            className="channel-box"
                            colClassname="room-photo-grid"
                            blockClass="photo-select-border-box channel-border-box"
                            src={ photo.photo_url_thump}
                            value={photo.id}
                            defaultChecked={photo.is_selected}
                            onClick={ (e) => updateRoomPhoto(e,roomId)}
                            labelClass="fact-name"
                            htmlFor={photo.id}
                        />
                    ) : (
                        <></>
                    )
                )
            );

            return roomPhotoListBox;
        }

    };

    const updateRoomPhoto = (e,placeId) => {
        const photoId      = e.target.value
        const photoChecked = e.target.checked

        if( photoId === undefined && photoChecked === undefined ) {
            return;
        }

        const url = UPDATE_PLACE_PHOTO;

        const data = ({
            params: {
                property_id: getProperty(),
                place_id: placeId,
                property_place_photo: photoId.split(',').map( (x) => (
                    { id :+x,
                        is_selected:  e.target.checked}
                ))
            }
        });

        axios.post(url, data,{
            headers: {
                "Accept": "application/json",
                authToken: getUserToken(),
                "Content-Type": "application/json",
                "language": getAppLang(),
            }
        }).then((result) => {
        })
            .catch(function (error) {
                console.log(error);
            });
    };

    if(placeList && placeList.length === 0 )
    {
        return (
            <h2>{ t('enw-notification-no_results') }</h2>
        )
    }else{
        return (

            <>

                {
                    props.selectedPlaceCategoryId === 0 ?
                        placeList && placeList.length && placeList.map( (place) => (
                            <ExpansionPanel className={place?.status === 1 ? "accordion-room-table" : "accordion-room-table inactive-plcs"} key={place.id} expanded={expanded === place.id} onChange={handleChange(place.id)}>
                                <ExpansionPanelSummary
                                    expandIcon={<i className="fas fa-caret-down" />}
                                    aria-label="Expand">
                                    <ColAtm>
                                        <ColAtm md={10} xl={11}>
                                            <i className="far fa-archway room-rate-bed" />
                                            <LabelAtm labelClass="room-rate-list-title" text={place.name} />
                                        </ColAtm>
                                    </ColAtm>
                                </ExpansionPanelSummary>
                                <ExpansionPanelDetails className="room-rate-detail MuiGrid-root MuiGrid-container MuiGrid-item">
                                    <ColAtm>
                                        {
                                            placePhotoList && placePhotoList.length > 0 && placePhotoList.show_recommended === true && <ColAtm className="recommended-box">
                                                <ColAtm xs={6} md={2}>
                                                    <LabelAtm text={ t('enw-room-recommended') } />
                                                </ColAtm>
                                                <CheckboxGroup>
                                                    <ColAtm xs={6} md={10} className="select-all">
                                                        <label className="select-all-chck">
                                                            <AllCheckerCheckbox
                                                                value={[
                                                                    placePhotoList.get_room_photos.map( (photo, key) => (
                                                                        photo.is_recommended === true && photo.is_recommended != "false"  && photo.is_recommended != false ?
                                                                            [photo.id + (",")]
                                                                            :
                                                                            null
                                                                    )).join('').slice(0, -1)
                                                                ]}
                                                                onChange={ (e) => updateRoomPhoto(e, place.id)}
                                                            />
                                                            <span className="select-all-text">
                                                                        { t('btn-deselect_all') }
                                                                    </span>
                                                            <span className="deselect-all-text">
                                                                        { t('btn-select_all') }
                                                                    </span>
                                                        </label>
                                                    </ColAtm>
                                                    { expanded === place.id ? RoomRecommendedPhotoList(place.id) : '' }
                                                </CheckboxGroup>
                                            </ColAtm>
                                        }
                                        <ColAtm innerRef={ expanded === place.id ? photoGridRef : undefined }>
                                            { expanded === place.id ? RoomPhotoList(place.id) : '' }
                                        </ColAtm>
                                    </ColAtm>
                                </ExpansionPanelDetails>
                            </ExpansionPanel>
                        )) :
                        placeList && placeList.length && placeList.filter( (place) => place.category_filters.includes(props.selectedPlaceCategoryId)  )
                            .map( (place) =>(
                                <ExpansionPanel className={place?.status === 1 ? "accordion-room-table" : "accordion-room-table inactive-plcs"} key={place.id} expanded={expanded === place.id} onChange={handleChange(place.id)}>
                                    <ExpansionPanelSummary
                                        expandIcon={<i className="fas fa-caret-down" />}
                                        aria-label="Expand">
                                        <ColAtm>
                                            <ColAtm md={10} xl={11}>
                                                <i className="far fa-archway room-rate-bed" />
                                                <LabelAtm labelClass="room-rate-list-title" text={place.name} />
                                            </ColAtm>
                                        </ColAtm>
                                    </ExpansionPanelSummary>
                                    <ExpansionPanelDetails className="room-rate-detail MuiGrid-root MuiGrid-container MuiGrid-item">
                                        <ColAtm>
                                            {
                                                placePhotoList && placePhotoList.length > 0 && placePhotoList.show_recommended === true && <ColAtm className="recommended-box">
                                                    <ColAtm xs={6} md={2}>
                                                        <LabelAtm text={ t('enw-room-recommended') } />
                                                    </ColAtm>
                                                    <CheckboxGroup>
                                                        <ColAtm xs={6} md={10} className="select-all">
                                                            <label className="select-all-chck">
                                                                <AllCheckerCheckbox
                                                                    value={[
                                                                        placePhotoList.get_room_photos.map( (photo, key) => (
                                                                            photo.is_recommended === true && photo.is_recommended != "false"  && photo.is_recommended != false ?
                                                                                [photo.id + (",")]
                                                                                :
                                                                                null
                                                                        )).join('').slice(0, -1)
                                                                    ]}
                                                                    onChange={ (e) => updateRoomPhoto(e, place.id)}
                                                                />
                                                                <span className="select-all-text">
                                                                        { t('btn-deselect_all') }
                                                                    </span>
                                                                <span className="deselect-all-text">
                                                                        { t('btn-select_all') }
                                                                    </span>
                                                            </label>
                                                        </ColAtm>
                                                        { expanded === place.id ? RoomRecommendedPhotoList(place.id) : '' }
                                                    </CheckboxGroup>
                                                </ColAtm>
                                            }
                                            <ColAtm innerRef={ expanded === place.id ? photoGridRef : undefined }>
                                                { expanded === place.id ? RoomPhotoList(place.id) : '' }
                                            </ColAtm>
                                        </ColAtm>
                                    </ExpansionPanelDetails>
                                </ExpansionPanel>
                            ))
                }


            </>
        );
    }


};


export default PlacePhotoList;
